import { useMemo } from "react";
import type React from "react";
import styles from "./ProductAnalyticsPage.module.scss";
import AnalyticsProgressCard from "../../../common/components/analytics-component/progress-card/AnalyticsProgressCard";
import AnalyticsLineChart from "../../../common/components/analytics-component/line-chart/AnalyticsLineChart";
import AnalyticsBarList, { AnalyticsBarRow } from "../../../common/components/analytics-component/bar-list/AnalyticsBarList";
import AnalyticsHeatmap from "../../../common/components/analytics-component/heatmap/AnalyticsHeatmap";
import { RETENTION_COLUMNS, vendRetentionRows } from "./Retention.constants";
import AnalyticsTable, { AnalyticsTableColumn } from "../../../common/components/analytics-component/table/AnalyticsTable";
import AnalyticsMessage from "../../../common/components/analytics-component/message/AnalyticsMessage";
import Badge, { BadgeVariant } from "../../../common/components/xepp/badge/Badge";
import AnalyticsFilters from "../../../common/components/analytics-component/filters/AnalyticsFilters";
import AnalyticsPageLayout from "../../../common/components/analytics-component/page-layout/AnalyticsPageLayout";
import { ALL_PLATFORMS, useAnalyticsFilters } from "../../../common/components/analytics-component/filters/useAnalyticsFilters";
import {
	useGetDropOffPointsQuery,
	useGetFeatureAdoptionQuery,
	useGetFlowFunnelQuery,
	useGetPlatformUsersQuery,
	useGetSessionsOverviewQuery,
	useGetVendRetentionQuery,
	useGetWeeklyFlagsQuery,
} from "../../../service/business-analytics-service";
import { DropOffPoint, DropOffStatus, PlatformUsersKey } from "../../../model/bps-admin/business-analytics";
import AnalyticsCard, { AnalyticsCardType } from "../../../common/components/analytics-component/card/AnalyticsCard";
import AnalyticsHeader from "../../../common/components/analytics-component/header/AnalyticsHeader";
import AnalyticsState from "../../../common/components/analytics-component/state/AnalyticsState";

// The colour slot is pinned per platform, so narrowing the filter to one does not
// hand it another platform's colour.
const PLATFORM_SERIES: { key: PlatformUsersKey; label: string; colorSlot: number }[] = [
	{ key: "android", label: "Android", colorSlot: 0 },
	{ key: "ios", label: "iOS", colorSlot: 1 },
	{ key: "web", label: "Web", colorSlot: 2 },
];

// The date part is parsed into local time, for the same reason the filters build
// their range out of local parts rather than through toISOString.
const dayLabel = (day: string) => {
	const [year, month, date] = day.slice(0, 10).split("-").map(Number);
	return new Date(year, month - 1, date).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

// Which flow the funnel shows — a guess at the backend's code until it is confirmed.
const VEND_FLOW_CODE = "payment_vend";

// The badge variant each status is drawn in; "new" is one nobody has triaged yet.
const DROP_OFF_STATUS_VARIANTS: Record<DropOffStatus, BadgeVariant> = {
	new: "active",
	investigate: "failed",
	watch: "pending",
};

/**
 * Lower-cased before the lookup: the response's casing is not guaranteed, and a status
 * that missed the map would index to undefined, reach the class name as the string
 * "undefined", and paint the badge no colour at all. A status outside the three falls
 * back to grey, which claims nothing about it.
 */
const statusVariant = (status: string): BadgeVariant =>
	(DROP_OFF_STATUS_VARIANTS as Record<string, BadgeVariant | undefined>)[status.toLowerCase()] ?? "inactive";

const DROP_OFF_COLUMNS: AnalyticsTableColumn<DropOffPoint>[] = [
	{ header: "Screen / step", render: point => `${point.flowName} · ${point.stepName}` },
	{ header: "Entered", align: "right", render: point => point.entered.toLocaleString() },
	{ header: "Exit rate", align: "right", render: point => `${point.percentage}%` },
	{
		header: "Status",
		render: point => <Badge variant={statusVariant(point.status)}>{point.status.toLowerCase()}</Badge>,
	},
];

// "00:21:23.3333333" -> "21m 23s". The fractional seconds are dropped, and so is a
// zero hour: "0h" is noise on a session length.
const sessionLength = (timeSpan: string) => {
	const [hours, minutes, seconds] = timeSpan.split(":").map(Number);
	const rest = `${minutes}m ${Math.floor(seconds)}s`;
	return hours > 0 ? `${hours}h ${rest}` : rest;
};

// Same dash the heatmap puts in a cell it has no number for.
const EMPTY_VALUE = "–";

/** A figure that has not arrived reads as absent rather than as a number. */
const format = <T,>(value: T | null | undefined, render: (value: T) => string) =>
	value === null || value === undefined ? EMPTY_VALUE : render(value);

// The change arrives signed, so the title says which way it went rather than printing a
// minus, and the tone follows. Zero is also what the backend sends when last week had no
// signups at all.
const signupsFlag = (change: number): { tone: AnalyticsCardType; title: string } => {
	if (change > 0) return { tone: "positive", title: `Signups up ${change}% week-on-week` };
	if (change < 0) return { tone: "warning", title: `Signups down ${Math.abs(change)}% week-on-week` };
	return { tone: "neutral", title: "Signups flat week-on-week" };
};

// Product analytics of the BPS admin portal. Shell only — the widgets and the
// data it shares with the BPS admin dashboard land with the analytics work.
const ProductAnalyticsPage = () => {
	const { platform, range, rangeWithPlatform, periodLabel, platformLabel, controls } = useAnalyticsFilters();

	// One pair of flags per widget rather than one for the page: the queries are
	// independent, so a failing overview must not take the chart down with it.
	const {
		data: platformUsers,
		isLoading: isLoadingUsers,
		isFetching: isRefetchingUsers,
		isError: isUsersError,
		refetch: refetchUsers,
	} = useGetPlatformUsersQuery(range);
	const {
		data: overview,
		isLoading: isLoadingOverview,
		isFetching: isRefetchingOverview,
		isError: isOverviewError,
		refetch: refetchOverview,
	} = useGetSessionsOverviewQuery(rangeWithPlatform);

	// Value only: the overview carries no earlier period, so there is no trend to put
	// in the badge and nothing for the bar to be a share of. The cards are built even
	// when the overview is missing, so the row keeps its shape instead of appearing.
	const cards = useMemo<React.ComponentProps<typeof AnalyticsProgressCard>[]>(
		() => [
			{ label: "Active users (DAU)", value: format(overview?.activeUsers, count => count.toLocaleString()) },
			{ label: "Stickiness (DAU/MAU)", value: format(overview?.stickiness, share => `${share}%`) },
			{ label: "New signups", value: format(overview?.newSignups, count => count.toLocaleString()) },
			{ label: "Vend conversion", value: format(overview?.vendConversion, share => `${share}%`) },
			{ label: "Paid · no token", value: format(overview?.noToken, share => `${share}%`) },
			{ label: "Avg session", value: format(overview?.averageSession, sessionLength) },
		],
		[overview]
	);

	const chartLabels = useMemo(() => (platformUsers ?? []).map(point => dayLabel(point.day)), [platformUsers]);

	// All platforms means one series each; picking one narrows the chart to it.
	const chartSeries = useMemo(
		() =>
			PLATFORM_SERIES.filter(series => platform === ALL_PLATFORMS || series.key === platform).map(series => ({
				label: series.label,
				colorSlot: series.colorSlot,
				data: (platformUsers ?? []).map(point => point[series.key]),
			})),
		[platform, platformUsers]
	);

	const {
		data: vendFunnel,
		isLoading: isLoadingFunnel,
		isFetching: isRefetchingFunnel,
		isError: isFunnelError,
		refetch: refetchFunnel,
	} = useGetFlowFunnelQuery({ code: VEND_FLOW_CODE, getErrors: false, ...rangeWithPlatform });
	const {
		data: featureAdoption,
		isLoading: isLoadingAdoption,
		isFetching: isRefetchingAdoption,
		isError: isAdoptionError,
		refetch: refetchAdoption,
	} = useGetFeatureAdoptionQuery(rangeWithPlatform);
	const {
		data: dropOffPoints,
		isLoading: isLoadingDropOff,
		isFetching: isRefetchingDropOff,
		isError: isDropOffError,
		refetch: refetchDropOff,
	} = useGetDropOffPointsQuery(rangeWithPlatform);
	const {
		data: vendRetention,
		isLoading: isLoadingRetention,
		isFetching: isRefetchingRetention,
		isError: isRetentionError,
		refetch: refetchRetention,
	} = useGetVendRetentionQuery(rangeWithPlatform);
	// No range: the flags cover this week whatever the period filter says, so moving the
	// period must not refetch them.
	const {
		data: flags,
		isLoading: isLoadingFlags,
		isFetching: isRefetchingFlags,
		isError: isFlagsError,
		refetch: refetchFlags,
	} = useGetWeeklyFlagsQuery({ utilityId: rangeWithPlatform.utilityId, platform: rangeWithPlatform.platform });

	const retentionRows = useMemo(() => vendRetentionRows(vendRetention), [vendRetention]);

	// Sorted here: the section reads as a ranking and the response carries no order
	// field. The adoption count is not shown — the design's right column is the share.
	const adoptionRows = useMemo<AnalyticsBarRow[]>(
		() =>
			[...(featureAdoption ?? [])]
				.sort((first, second) => second.percentage - first.percentage)
				.map(flow => ({
					label: flow.flowName,
					percent: flow.percentage,
					trailing: `${flow.percentage}%`,
				})),
		[featureAdoption]
	);

	// Sorted here for the same reason feature adoption is: the heading calls these the
	// top drop-offs and the response carries no order field. By share rather than by
	// count — a step only 200 people reach can still be the worst one.
	const dropOffRows = useMemo<DropOffPoint[]>(
		() => [...(dropOffPoints ?? [])].sort((first, second) => second.percentage - first.percentage),
		[dropOffPoints]
	);

	// The response carries no tone, so every stage reads neutral — see the bar list's
	// own props for what marks a row as the problem.
	const funnelRows = useMemo<AnalyticsBarRow[]>(
		() =>
			[...(vendFunnel?.steps ?? [])]
				.sort((first, second) => first.order - second.order)
				.map(step => ({
					label: step.name,
					percent: step.percentage,
					insideLabel: `${step.percentage}%`,
					trailing: step.reached.toLocaleString(),
				})),
		[vendFunnel]
	);

	return (
		<AnalyticsPageLayout title="Product Analytics" filters={<AnalyticsFilters {...controls} />}>
			{/* The row has no card of its own to hold a heading, so the state stands in
			    for the whole row rather than for each card's value. */}
			{/* Empty is the whole response missing, not a figure of zero — zero active users
			    is a measurement, and the cards are right to print it. */}
				<div className={styles.mainContent}>
					
			<AnalyticsState
				loading={isLoadingOverview}
				error={isOverviewError}
				onRetry={refetchOverview}
				retrying={isRefetchingOverview}
				empty={!overview}
			>
				<div className={styles.cardRow}>
					{cards.map(card => (
						<AnalyticsProgressCard key={card.label} {...card} />
					))}
				</div>
			</AnalyticsState>
			<AnalyticsLineChart
				title="Daily active users"
				subtitle={`${platform === ALL_PLATFORMS ? "By platform" : platformLabel} · ${periodLabel.toLowerCase()}`}
				labels={chartLabels}
				series={chartSeries}
				loading={isLoadingUsers}
				error={isUsersError}
				onRetry={refetchUsers}
				retrying={isRefetchingUsers}
			/>
			<div className={styles.cardsContainer}>
				{/* Rendered unconditionally now: a widget that disappears on failure takes the
				    news of the failure with it. The flow's own name replaces the fallback
				    once the response names it. */}
				<AnalyticsBarList
					title={vendFunnel?.flowName ?? "Vend funnel"}
					subtitle={vendFunnel ? `${vendFunnel.flowsStarted.toLocaleString()} flows started` : undefined}
					rows={funnelRows}
					loading={isLoadingFunnel}
					error={isFunnelError}
					onRetry={refetchFunnel}
					retrying={isRefetchingFunnel}
				/>

				<AnalyticsHeatmap
					title="Retention by weekly cohort"
					subtitle={`% returning ${RETENTION_COLUMNS[0]} -> ${RETENTION_COLUMNS[RETENTION_COLUMNS.length - 1]}`}
					columns={RETENTION_COLUMNS}
					rows={retentionRows}
					unit="%"
					loading={isLoadingRetention}
					error={isRetentionError}
					onRetry={refetchRetention}
					retrying={isRefetchingRetention}
				/>
				<AnalyticsBarList
					title="Feature adoption"
					subtitle={`Share of active users, ${periodLabel.toLowerCase()}`}
					rows={adoptionRows}
					loading={isLoadingAdoption}
					error={isAdoptionError}
					onRetry={refetchAdoption}
					retrying={isRefetchingAdoption}
				/>
				<AnalyticsTable
					title="Top drop-off points"
					subtitle="Highest exit before completing a vend"
					columns={DROP_OFF_COLUMNS}
					rows={dropOffRows}
					loading={isLoadingDropOff}
					error={isDropOffError}
					onRetry={refetchDropOff}
					retrying={isRefetchingDropOff}
				/>
				{/* Only what the response measures is said: it carries no platform split and does
				    not rank the auth step against the other drops, so neither is claimed. */}
				<AnalyticsCard>
					<AnalyticsHeader title="Flags this week" subtitle="Auto-surfaced from the money path" />
					<AnalyticsState
						loading={isLoadingFlags}
						error={isFlagsError}
						onRetry={refetchFlags}
						retrying={isRefetchingFlags}
						empty={!flags}
					>
						{flags && (
							<>
								<AnalyticsMessage
									tone="negative"
									title={`Paid, but no token — ${flags.noTokenVends.toLocaleString()} vends (${flags.noTokenPercentage}%)`}
								>
									{format(flags.utility, name => name)} · each is a successful charge with no electricity delivered —
									the clearest trust-and-refund risk.
								</AnalyticsMessage>
								<AnalyticsMessage title={`${flags.authAbandonPercentage}% abandon at the auth step`}>
									Chose a payment method but never got an authorisation result. Worth a UX pass before it caps vend
									conversion further.
								</AnalyticsMessage>
								<AnalyticsMessage {...signupsFlag(flags.signupsChangePercentage)}>
									This week so far, against the same days last week.
								</AnalyticsMessage>
							</>
						)}
					</AnalyticsState>
				</AnalyticsCard>
			</div>
			</div>
		</AnalyticsPageLayout>
	);
};

export default ProductAnalyticsPage;
